import { t } from "@lingui/core/macro";
import { secToMs } from "../converter";
import type { ErrorKeys } from "../../../components/Errors/ErrorContainer";

export type StopButtonVariants = "simple" | "complex";
export type IdleMessageStyle = "inline" | "popup";
export type startMessageTime = "inline" | "down" | "none";
export type PartnerSetting = {
  misc: {
    keyword: string;
    phoneNumber: string;
    customHomepageRoute?: string;
    whatsAppNumber?: string;
  };
  timeouts: {
    timeoutReminder: number;
    timeToTimeout: number;
    failMessageAfter: number;
    timeToClearConversationAfterClose: number;
    clearConversationAfterClose: boolean;
  };
  stopButtonVariant: StopButtonVariants;
  sendMessageButtonColor: "secondary" | "primary";
  idleMessageStyle: IdleMessageStyle;
  // This message is to reduce confusion about getting disconnected when on a
  // mobile device and navigating to a different app
  startMessage: {
    showStartMessageText: boolean;
    showTime: startMessageTime;
  };
  autoStartConversation: boolean;
  showMenu: boolean;
  tracking: {
    trackWindowClosed: boolean;
  };
  text: {
    ui: {
      startMessage: {
        startMessageText: string;
        startMessageTitle?: string;
      };
      stopButtonText: { [K in StopButtonVariants]: string };
      disconnectDisclaimer: string;
      cancelButtonText: string;
      okButtonText: string;
      chatboxLabelText: string;
      chatboxTooShortPreText: string;
      chatboxTooShortPostText: string;
      chatboxTooLongPreText: string;
      chatboxTooLongPostText: string;
      closeConvoPreText: string;
      closeConvoPostText: string;
      sendMessageButtonText: string;
      closeConversationDialog: {
        leaveChat: string;
        leaveChatCompact: string;
      };
      yourConversationHasBeenClosed: string;
    };
    staticPages: {
      [key in ErrorKeys]: {
        description: string;
      };
    };
  };
};

export const DEFAULT_TIMEOUT = 60;
export const DEFAULT_TIMEOUT_REMINDER = 55;
export const DEFAULT_FAIL_MESSAGE_AFTER = secToMs(10);

export const staticPageDefaultContent = {
  generic: {
    description: t({
      id: "app.static-pages.generic.description.default",
      message:
        "Include a description of the error and clear action the user may take to solve it (or a workaround).",
    }),
  },
  noToken: {
    description: t({
      id: "app.static-pages.no-token.description.default",
      message:
        "Webchat support is not available through your link. You are brave to reach out, and we're still here for you. Please check your internet connection and try again. If that doesn't work, text STUDENT to 741741.",
    }),
  },
  disconnected: {
    description: t({
      id: "app.static-pages.disconnected.description.default",
      message: "You have been disconnected. Please get another link.",
    }),
  },
  cannotConnect: {
    description: t({
      id: "app.static-pages.cannot-connect.description.default",
      message:
        "Webchat support is not available through your link. You are brave to reach out, and we're still here for you. Please check your internet connection and try again. If that doesn't work, text STUDENT to 741741.",
    }),
  },
  openedInAnotherTab: {
    description: t({
      id: "app.static-pages.opened-in-another-tab.description.default",
      message:
        "Want to chat here instead? \nRefresh this page to continue your conversation.",
    }),
  },
  conversationCleared: {
    description: t({
      id: "app.static-pages.conversation-cleared.description.default",
      message:
        "You have been disconnected and conversation is cleared. Please get another link.",
    }),
  },
  notFound: {
    description: t({
      id: "app.static-pages.not-found.description.default",
      message: "We can't find what you're looking for.",
    }),
  },
};

export const cannotConnectDescCTL = t({
  id: "app.static-pages.cannot-connect.description.ctl-default",
  message:
    "Webchat support is not available. Please check your internet connection and try again. If that doesn’t work, you can <a href='sms:741741?&body=HOME'>text HOME to 741741</a> or reach us via <a href='https://wa.me/14437877678?text=HOME&lang=en'>WhatsApp.</a>",
});
